import React from "react";
import { default as BootstrapModal } from "react-bootstrap/Modal";
import Button from "@mui/material/Button";
import ContentItemList from "./ContentItemList";
import ContentItemForm from "./ContentItemForm";
import { useAutoGenPolling } from "../../../hooks/useAutoGenPolling";

import styles from "./virtualBlockContentModal.module.scss";

/**
 * VirtualBlockContentModal Component
 * Modal for managing multiple content items for a virtual block
 *
 * @param {Object} props
 * @param {string} props.selectedLabel - The chosen block label (e.g., "Notes 📝", "Recall")
 * @param {string} props.iconLocation - The icon position (TL, TM, TR, L1-R6, BL, BM, BR)
 * @param {Array} props.existingContents - Array of existing content items
 * @param {string} [props.pageImageUrl] - URL of the current page image (for AutoGen crop)
 * @param {string[]} [props.allowedTypes] - Content types to offer (default: all); used by reader mode
 * @param {Function} props.onSave - Save handler, receives array of contents
 * @param {Function} props.handleCloseModal - Close modal callback
 */
const VirtualBlockContentModal = (props) => {
  const {
    selectedLabel = "",
    iconLocation = "",
    existingContents = [],
    pageImageUrl,
    allowedTypes,
    onSave,
    handleCloseModal,
  } = props;

  // Local state for managing content items
  const [contents, setContents] = React.useState(existingContents);
  // Show form initially if no existing contents (new virtual block)
  const [showForm, setShowForm] = React.useState(existingContents.length === 0);
  const [editingIndex, setEditingIndex] = React.useState(null);

  // Apply a partial update to a content item (used by polling hook)
  const handleUpdateContent = React.useCallback((index, patch) => {
    setContents((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], ...patch };
      return updated;
    });
  }, []);

  // Poll pending autogen items while the modal is open
  useAutoGenPolling(contents, handleUpdateContent);


  /**
   * Handle adding new content item
   */
  const handleAddContent = (contentItem) => {
    // Spread all fields so autogen-specific ones (jobId, status, objectId, etc.) are kept
    const newContentItem = {
      ...contentItem,
      iconLocation: iconLocation,
      contentType: selectedLabel,
    };

    if (editingIndex !== null) {
      // Update existing item, keeping its identity (reader blocks carry id/createdAt)
      const { id, createdAt } = contents[editingIndex] || {};
      const updatedContents = [...contents];
      updatedContents[editingIndex] = {
        ...(id && { id }),
        ...(createdAt && { createdAt }),
        ...newContentItem,
      };
      setContents(updatedContents);
      setEditingIndex(null);
    } else {
      // Add new item
      setContents([...contents, newContentItem]);
    }

    // Hide form after adding
    setShowForm(false);
  };

  /**
   * Handle editing content item
   */
  const handleEditContent = (index) => {
    setEditingIndex(index);
    setShowForm(true);
  };

  /**
   * Handle deleting content item
   */
  const handleDeleteContent = (index) => {
    const updatedContents = contents.filter((_, i) => i !== index);
    setContents(updatedContents);
  };

  /**
   * Handle cancel form
   */
  const handleCancelForm = () => {
    setShowForm(false);
    setEditingIndex(null);
  };

  /**
   * Handle save all contents
   */
  const handleSaveAll = () => {
    if (onSave) {
      onSave(contents);
    }
    // Modal will be closed by parent
  };

  /**
   * Handle cancel modal
   */
  const handleCancel = () => {
    if (handleCloseModal) {
      handleCloseModal();
    }
  };

  // Get content being edited
  const editingContent = editingIndex !== null ? contents[editingIndex] : null;

  return (
    <div
      className={styles["virtual-block-content-modal"]}
      role="dialog"
      aria-labelledby="virtual-block-modal-title"
      aria-describedby="virtual-block-modal-description"
    >
      <BootstrapModal.Header closeButton>
        <BootstrapModal.Title id="virtual-block-modal-title">
          Virtual Block: {selectedLabel}
        </BootstrapModal.Title>
      </BootstrapModal.Header>

      <BootstrapModal.Body>
        <div
          className={styles.container}
          id="virtual-block-modal-description"
        >
          {/* Existing Content Items List */}
          {contents.length > 0 && (
            <div className={styles.section} role="region" aria-label="Existing content items">
              <h6 className={styles.sectionTitle}>
                Content Items ({contents.length})
              </h6>
              <ContentItemList
                contents={contents}
                selectedLabel={selectedLabel}
                onEdit={handleEditContent}
                onDelete={handleDeleteContent}
              />
            </div>
          )}

          {/* Add New Content Button */}
          {!showForm && (
            <div className={styles.section}>
              <Button
                variant="outlined"
                color="primary"
                onClick={() => setShowForm(true)}
                fullWidth
                aria-label="Add new content item"
              >
                + Add New Content
              </Button>
            </div>
          )}

          {/* Content Item Form */}
          {showForm && (
            <div
              className={styles.section}
              role="region"
              aria-label={editingIndex !== null ? "Edit content form" : "Add content form"}
            >
              <h6 className={styles.sectionTitle}>
                {editingIndex !== null ? "Edit Content" : "Add Content"}
              </h6>
              <ContentItemForm
                selectedLabel={selectedLabel}
                iconLocation={iconLocation}
                editingContent={editingContent}
                pageImageUrl={pageImageUrl}
                allowedTypes={allowedTypes}
                onSubmit={handleAddContent}
                onCancel={handleCancelForm}
              />
            </div>
          )}
        </div>
      </BootstrapModal.Body>

      <BootstrapModal.Footer style={{ display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          color="secondary"
          onClick={handleCancel}
          aria-label="Cancel and close modal"
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSaveAll}
          aria-label={`Save all ${contents.length} content items`}
        >
          Save All
        </Button>
      </BootstrapModal.Footer>
    </div>
  );
};

export default VirtualBlockContentModal;
